import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';

/**
 * Prueba de Santiago en producción (ferretest, 2026-09-14): al abrir el POS,
 * `GET /api/inventory/identidad/familias` dio 504 y después un «error de CORS».
 * No era CORS: el inventario estaba dormido y, mientras despertaba, el borde
 * respondió con una página sin cabeceras. Santiago decidió que el POS reintente
 * en vez de pagar por tener el servicio despierto.
 *
 * Solo para lecturas del inventario al arrancar la caja, llamada por llamada.
 * Nunca como interceptor global: reintentar solo un POST de venta es cobrar dos veces.
 */
export const TEXTO_INVENTARIO_ARRANCANDO = 'El inventario está arrancando, intenta de nuevo en unos segundos';

/** Esperas antes de cada reintento: tres reintentos tras el primer intento. */
export const ESPERAS_AL_DESPERTAR_MS: readonly number[] = [1000, 3000, 6000];

export class InventarioArrancandoError extends Error {
  constructor(readonly causa: unknown) {
    super(TEXTO_INVENTARIO_ARRANCANDO);
  }
}

/** La espera entre intentos. Se sustituye en las pruebas; es la única espera y vive dentro del intento. */
export const relojDeReintentos = {
  esperar: (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
};

/** Un servicio despertando: el borde responde 502/503/504, o no llega respuesta (status 0). */
export function esErrorDeArranque(e: unknown): boolean {
  return e instanceof HttpErrorResponse && [0, 502, 503, 504].includes(e.status);
}

export async function conReintentoAlDespertar<T>(pedir: () => Promise<T>): Promise<T> {
  for (let intento = 0; ; intento++) {
    try {
      return await pedir();
    } catch (e) {
      if (!esErrorDeArranque(e)) throw e;
      if (intento >= ESPERAS_AL_DESPERTAR_MS.length) throw new InventarioArrancandoError(e);
      await relojDeReintentos.esperar(ESPERAS_AL_DESPERTAR_MS[intento]);
    }
  }
}

/** Si el inventario agotó los reintentos al arrancar. Lo lee la caja para decirlo. */
@Injectable({ providedIn: 'root' })
export class EstadoDelInventario {
  readonly aviso = signal<string | null>(null);

  arrancando(): void {
    this.aviso.set(TEXTO_INVENTARIO_ARRANCANDO);
  }

  listo(): void {
    this.aviso.set(null);
  }
}
